<div class="appointments-page">
  @if (isLoading) {
    <div class="appointments-loading-bar" aria-label="Loading appointments">
      <div class="appointments-loading-bar__track">
        <div class="appointments-loading-bar__value"></div>
      </div>
    </div>
  }

  <section class="overview-hero">
    <div class="hero-copy">
      <span class="hero-kicker">Business Schedule</span>

      <h1>Manage <span>Appointments</span></h1>

      <p>
        Manage the live salon schedule by staff member. Review working hours,
        time off, bookings, and reassign appointments directly from the
        schedule.
      </p>

      <div class="salon-scope-pill">
        <span>Salon view</span>
        <strong>{{ salonContext.selectionLabel() }}</strong>
      </div>
    </div>

    <div class="hero-actions">
      <button
        type="button"
        class="overview-btn overview-btn--dark"
        (click)="openAddAppointmentModal()"
      >
        Add Appointment
      </button>
    </div>
  </section>

  <section class="kpi-grid">
    <article class="kpi-card kpi-card--primary">
      <div>
        <span>Total Appointments</span>
        <strong>{{ appointmentsData.length }}</strong>
        <p>Total bookings currently loaded for your business.</p>
      </div>

      <img [src]="assetUrl + 'icons/calendar-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Pending</span>
        <strong>{{ pendingCount }}</strong>
        <p>Bookings waiting for confirmation or action.</p>
      </div>

      <img [src]="assetUrl + 'icons/calendar-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Confirmed</span>
        <strong>{{ confirmedCount }}</strong>
        <p>Appointments confirmed and ready for service.</p>
      </div>

      <img [src]="assetUrl + 'icons/users-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Completed</span>
        <strong>{{ completedCount }}</strong>
        <p>Bookings completed successfully.</p>
      </div>

      <img [src]="assetUrl + 'icons/smile-border.svg'" alt="" />
    </article>
  </section>

  <section class="panel">
    <header class="panel-head">
      <div>
        <span class="panel-kicker">Appointment Workspace</span>

        <h2>Schedule & Booking List</h2>

        <p>
          Use the staff schedule for daily operations or switch to the table for
          booking administration.
        </p>
      </div>
    </header>

    <glo-tabs [tabs]="tabs" [(activeTab)]="activeTab" [scrollingMode]="false">
      <!-- =====================================================
           CALENDAR / SCHEDULE
           ===================================================== -->

      <ng-template gloTabContent="calendar">
        <div class="schedule-toolbar">
          <div class="date-summary">
            <div class="date-card">
              <span>{{ scheduleDate | date: "MMM" | uppercase }}</span>
              <strong>{{ scheduleDate | date: "d" }}</strong>
            </div>

            <div>
              <h3>{{ scheduleTitle }}</h3>

              <p>
                @if (calendarView === "day") {
                  Drag an appointment to another employee or time to reschedule
                  it.
                } @else if (calendarView === "week") {
                  Review the week's bookings and select a day for the detailed
                  staff schedule.
                } @else {
                  Review bookings across the month and select a day to open its
                  staff schedule.
                }
              </p>
            </div>
          </div>

          <div class="toolbar-actions">
            <!-- Previous / Today / Next -->
            <div class="calendar-nav">
              <button
                type="button"
                (click)="previousPeriod()"
                [title]="previousButtonTitle"
              >
                <img [src]="assetUrl + 'icons/arrow-left-20.svg'" alt="" />
              </button>

              <button type="button" class="today-button" (click)="goToToday()">
                Today
              </button>

              <button
                type="button"
                (click)="nextPeriod()"
                [title]="nextButtonTitle"
              >
                <img [src]="assetUrl + 'icons/arrow-right-20.svg'" alt="" />
              </button>
            </div>

            <!-- Date picker -->
            <input
              class="filter-select schedule-date-input"
              type="date"
              [ngModel]="scheduleDateInput"
              (ngModelChange)="onScheduleDateInput($event)"
            />

            <!-- Calendar view -->
            <select
              class="filter-select"
              [(ngModel)]="calendarView"
              (ngModelChange)="onCalendarViewChange($event)"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>

            <!-- Salon -->
            <select
              class="filter-select"
              [(ngModel)]="selectedSalonId"
              (change)="onChangeSalon($event)"
            >
              <option value="all">All Selected Salons</option>

              @for (salon of salons; track salon.id) {
                <option [value]="salon.id">{{ salon.name }}</option>
              }
            </select>

            <!-- Appointment status -->
            <select
              class="filter-select"
              [(ngModel)]="selectedType"
              (change)="onChangeType($event)"
            >
              <option value="all">All Appointments</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No Show</option>
            </select>
          </div>
        </div>

        <!-- Schedule error -->
        @if (scheduleError) {
          <div class="schedule-message schedule-message--error">
            <strong>Unable to load the staff schedule</strong>
            <span>{{ scheduleError }}</span>
          </div>
        }

        <!-- Small inline loader -->
        @if (isScheduleLoading && calendarView === "day") {
          <div class="schedule-inline-loader">
            <span class="schedule-inline-loader__spinner"></span>

            <div>
              <strong>Loading schedule</strong>
              <span>Retrieving staff availability...</span>
            </div>
          </div>
        }

        <!-- =====================================================
             DAY VIEW
             ===================================================== -->

        @if (calendarView === "day" && !isScheduleLoading && !scheduleError) {
          @if (scheduleStaff.length === 0) {
            <div class="schedule-message">
              <strong>No staff found</strong>

              <span>
                Add active employees to the selected salon or change the salon
                filter.
              </span>
            </div>
          } @else {
            <div class="schedule-legend">
              <span>
                <i class="legend-dot legend-dot--working"></i>
                Working
              </span>

              <span>
                <i class="legend-dot legend-dot--off"></i>
                Unavailable / off
              </span>

              <span>
                <i class="legend-dot legend-dot--timeoff"></i>
                Time off
              </span>

              <span>
                <i class="legend-dot legend-dot--appointment"></i>
                Appointment
              </span>
            </div>

            <div class="staff-schedule-shell">
              <div
                class="staff-schedule"
                [style.--staff-count]="scheduleStaff.length"
                [style.--slot-height.px]="slotHeight"
              >
                <!-- Top-left corner -->
                <div class="schedule-corner">
                  <span>Time</span>
                </div>

                <!-- Staff headers -->
                @for (staff of scheduleStaff; track trackStaff($index, staff)) {
                  <div class="staff-header">
                    <div class="staff-avatar">
                      {{ getInitials(staff.name) }}
                    </div>

                    <div class="staff-header-copy">
                      <strong>{{ staff.name }}</strong>
                      <span>{{ staff.salonName }}</span>
                      <small>{{ getWorkingHoursLabel(staff) }}</small>
                    </div>
                  </div>
                }

                <!-- Time column -->
                <div class="time-column">
                  @for (slot of scheduleSlots; track trackSlot($index, slot)) {
                    <div class="time-slot-label">
                      <span>{{ formatScheduleTime(slot) }}</span>
                    </div>
                  }
                </div>

                <!-- Employee columns -->
                @for (staff of scheduleStaff; track trackStaff($index, staff)) {
                  <div class="staff-day-column">
                    <!-- Drop zones -->
                    @for (
                      slot of scheduleSlots;
                      track trackSlot($index, slot)
                    ) {
                      <div
                        class="drop-slot"
                        [class.drop-slot--off]="!isStaffWorkingAt(staff, slot)"
                        [class.drop-slot--timeoff]="
                          isStaffOnTimeOffAt(staff, slot)
                        "
                        [class.drop-slot--dragover]="
                          dragOverStaffId === staff.id &&
                          dragOverSlotKey === getSlotKey(slot)
                        "
                        (dragover)="onSlotDragOver($event, staff, slot)"
                        (dragleave)="onSlotDragLeave(staff, slot)"
                        (drop)="onAppointmentDrop($event, staff, slot)"
                      ></div>
                    }

                    <!-- Appointments -->
                    @for (
                      appointment of getAppointmentsForStaff(staff);
                      track trackAppointment($index, appointment)
                    ) {
                      <button
                        type="button"
                        class="schedule-appointment"
                        [ngClass]="getScheduleAppointmentClass(appointment)"
                        [style.top.px]="getAppointmentTop(appointment)"
                        [style.height.px]="getAppointmentHeight(appointment)"
                        [draggable]="canDragAppointment(appointment)"
                        (dragstart)="
                          onAppointmentDragStart($event, appointment)
                        "
                        (dragend)="onAppointmentDragEnd()"
                        (click)="openAppointmentModal(appointment)"
                        [title]="getAppointmentTooltip(appointment)"
                      >
                        <span class="appointment-time">
                          {{ formatTime(appointment.start_time) }}
                          –
                          {{ formatTime(appointment.end_time) }}
                        </span>

                        <strong>
                          {{ appointment.customer?.full_name || "Appointment" }}
                        </strong>

                        <span class="appointment-service">
                          {{ getCompactServiceNames(appointment) }}
                        </span>

                        <span class="appointment-status">
                          {{ formatStatusLabel(appointment.status) }}
                        </span>
                      </button>
                    }
                  </div>
                }
              </div>
            </div>
          }
        }

        <!-- =====================================================
             WEEK VIEW
             ===================================================== -->

        @if (calendarView === "week") {
          <div class="calendar-scroll">
            <div class="week-calendar">
              @for (day of weekDays; track trackCalendarDate($index, day)) {
                <div class="week-day" [class.week-day--today]="isToday(day)">
                  <button
                    type="button"
                    class="week-day__header"
                    (click)="openDay(day)"
                  >
                    <span>{{ day | date: "EEE" }}</span>
                    <strong>{{ day | date: "d" }}</strong>
                  </button>

                  <div class="week-day__body">
                    @for (
                      appointment of getAppointmentsForDate(day);
                      track appointment.appointment_id
                    ) {
                      <button
                        type="button"
                        class="calendar-booking"
                        [ngClass]="getCalendarBookingClass(appointment)"
                        (click)="openAppointmentModal(appointment)"
                      >
                        <span class="calendar-booking__time">
                          {{ formatTime(appointment.start_time) }}
                        </span>

                        <strong>
                          {{ appointment.customer?.full_name || "Appointment" }}
                        </strong>

                        <span>
                          {{ appointment.staff?.full_name || "Unassigned" }}
                        </span>

                        <small>
                          {{ getCompactServiceNames(appointment) }}
                        </small>
                      </button>
                    } @empty {
                      <button
                        type="button"
                        class="calendar-empty-day"
                        (click)="openDay(day)"
                      >
                        No bookings
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- =====================================================
             MONTH VIEW
             ===================================================== -->

        @if (calendarView === "month") {
          <div class="calendar-scroll">
            <div class="month-calendar">
              <div class="month-weekday">Sun</div>
              <div class="month-weekday">Mon</div>
              <div class="month-weekday">Tue</div>
              <div class="month-weekday">Wed</div>
              <div class="month-weekday">Thu</div>
              <div class="month-weekday">Fri</div>
              <div class="month-weekday">Sat</div>

              @for (day of monthDays; track trackCalendarDate($index, day)) {
                <div
                  class="month-day"
                  [class.month-day--outside]="!isCurrentMonth(day)"
                  [class.month-day--today]="isToday(day)"
                >
                  <button
                    type="button"
                    class="month-day__number"
                    (click)="openDay(day)"
                  >
                    {{ day | date: "d" }}
                  </button>

                  <div class="month-day__bookings">
                    @for (
                      appointment of getVisibleMonthAppointments(day);
                      track appointment.appointment_id
                    ) {
                      <button
                        type="button"
                        class="month-booking"
                        [ngClass]="getCalendarBookingClass(appointment)"
                        (click)="openAppointmentModal(appointment)"
                      >
                        <span>
                          {{ formatTime(appointment.start_time) }}
                        </span>

                        <strong>
                          {{ appointment.customer?.full_name || "Appointment" }}
                        </strong>
                      </button>
                    }

                    @if (getMonthOverflowCount(day) > 0) {
                      <button
                        type="button"
                        class="month-more"
                        (click)="openDay(day)"
                      >
                        +{{ getMonthOverflowCount(day) }} more
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }
      </ng-template>

      <!-- =====================================================
           TABLE VIEW
           ===================================================== -->

      <ng-template gloTabContent="table">
        <div class="table-container">
          <div class="table-controls">
            <glo-control
              icon="search"
              placeholder="Search appointments"
              classes="bg-gray-50"
              (valueChange)="onSearch($event)"
            >
            </glo-control>

            <select
              class="filter-select"
              [(ngModel)]="selectedSalonId"
              (change)="onChangeSalon($event)"
            >
              <option value="all">All Selected Salons</option>

              @for (salon of salons; track salon.id) {
                <option [value]="salon.id">{{ salon.name }}</option>
              }
            </select>

            <select
              class="filter-select"
              [(ngModel)]="selectedType"
              (change)="onChangeType($event)"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No Show</option>
            </select>
          </div>

          @if (error && !isLoading) {
            <div class="error-state">
              <strong>Unable to load appointments</strong>

              <p>{{ error }}</p>

              <button
                type="button"
                class="overview-btn overview-btn--dark"
                (click)="loadAppointments()"
              >
                Retry
              </button>
            </div>
          }

          @if (!isLoading && !error) {
            <div class="table-wrapper">
              <table class="appointments-table">
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Date</th>
                    <th>Salon</th>
                    <th>Staff</th>
                    <th>Service</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  @for (
                    appointment of paginatedAppointments;
                    track appointment.appointment_id
                  ) {
                    <tr>
                      <td class="booking-id">
                        {{ appointment.appointment_id }}
                      </td>

                      <td>
                        <div class="date-info">
                          <strong>
                            {{ formatDate(appointment.start_time) }}
                          </strong>

                          <span>
                            {{ formatTime(appointment.start_time) }}
                          </span>
                        </div>
                      </td>

                      <td>
                        {{ getSalonName(appointment) }}
                      </td>

                      <td>
                        <div class="customer-info">
                          <div class="customer-avatar">
                            {{
                              getInitials(
                                appointment.staff?.full_name || "Unknown"
                              )
                            }}
                          </div>

                          <div class="customer-details">
                            <strong>
                              {{
                                appointment.staff?.full_name || "Unknown Staff"
                              }}
                            </strong>

                            <span>
                              {{ appointment.staff?.email || "No email" }}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div class="service-info">
                          {{ getServiceNames(appointment) }}
                        </div>
                      </td>

                      <td>
                        <span
                          class="status-badge"
                          [ngClass]="getStatusClass(appointment.status)"
                        >
                          {{ formatStatusLabel(appointment.status) }}
                        </span>
                      </td>

                      <td>
                        <button
                          type="button"
                          class="action-button"
                          (click)="openAppointmentModal(appointment)"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>

              @if (paginatedAppointments.length === 0) {
                <div class="empty-state">
                  <div class="empty-icon">A</div>

                  <strong>No appointments found</strong>

                  <p>Try changing the search term, salon, or status filter.</p>
                </div>
              }
            </div>

            @if (totalItems > 0) {
              <glo-pagination
                [totalItems]="totalItems"
                [currentPage]="currentPage"
                [itemsPerPage]="itemsPerPage"
                [itemsPerPageOptions]="[5, 10, 20, 50]"
                (currentPageChange)="onPageChange($event)"
                (itemsPerPageChange)="onItemsPerPageChange($event)"
              >
              </glo-pagination>
            }
          }
        </div>
      </ng-template>
    </glo-tabs>
  </section>

  <div class="mobile-action">
    <button
      type="button"
      class="overview-btn overview-btn--dark"
      (click)="openAddAppointmentModal()"
    >
      Add Appointment
    </button>
  </div>

  <!-- =========================================================
       APPOINTMENT DETAILS
       ========================================================= -->

  @if (showModal) {
    <div class="modal-overlay" (click)="closeModal()">
      <div class="modal-container" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <div>
            <span class="panel-kicker">Appointment Details</span>

            <h3>Booking Information</h3>
          </div>

          <button type="button" class="modal-close" (click)="closeModal()">
            ×
          </button>
        </div>

        @if (selectedAppointment) {
          <div class="modal-body">
            <section class="detail-section">
              <h4>Basic Information</h4>

              <div class="detail-grid">
                <div class="detail-item">
                  <label>Booking ID</label>
                  <span>
                    {{ selectedAppointment.appointment_id }}
                  </span>
                </div>

                <div class="detail-item">
                  <label>Salon</label>
                  <span>
                    {{ getSalonName(selectedAppointment) }}
                  </span>
                </div>

                <div class="detail-item">
                  <label>Date & Time</label>
                  <span>
                    {{ formatDate(selectedAppointment.start_time) }}
                    at
                    {{ formatTime(selectedAppointment.start_time) }}
                  </span>
                </div>

                <div class="detail-item">
                  <label>Duration</label>
                  <span>
                    {{ getTotalDuration(selectedAppointment) }}
                  </span>
                </div>

                <div class="detail-item">
                  <label>Total Price</label>
                  <span>${{ getTotalPrice(selectedAppointment) }}</span>
                </div>

                <div class="detail-item">
                  <label>Status</label>

                  <span
                    class="status-badge"
                    [ngClass]="getStatusClass(selectedAppointment.status)"
                  >
                    {{ formatStatusLabel(selectedAppointment.status) }}
                  </span>
                </div>

                <div class="detail-item">
                  <label>Payment Status</label>

                  <span>
                    {{ formatStatusLabel(selectedAppointment.payment_status) }}
                  </span>
                </div>

                <div class="detail-item">
                  <label>Payment Method</label>

                  <span>
                    {{
                      formatPaymentMethod(selectedAppointment.payment_method)
                    }}
                  </span>
                </div>
              </div>
            </section>

            <section class="detail-section">
              <h4>Customer</h4>

              <div class="customer-detail">
                <div class="customer-avatar-large">
                  {{
                    getInitials(
                      selectedAppointment.customer?.full_name || "Unknown"
                    )
                  }}
                </div>

                <div>
                  <strong>
                    {{
                      selectedAppointment.customer?.full_name ||
                        "Unknown Customer"
                    }}
                  </strong>

                  <span>
                    {{ selectedAppointment.customer?.email || "No email" }}
                  </span>

                  <!-- <small>
                    {{ selectedAppointment.customer?.phone || "No phone" }}
                  </small> -->
                </div>
              </div>
            </section>

            <section class="detail-section">
              <h4>Staff</h4>

              <div class="customer-detail">
                <div class="customer-avatar-large">
                  {{
                    getInitials(
                      selectedAppointment.staff?.full_name || "Unknown"
                    )
                  }}
                </div>

                <div>
                  <strong>
                    {{
                      selectedAppointment.staff?.full_name || "Unknown Staff"
                    }}
                  </strong>

                  <span>
                    {{ selectedAppointment.staff?.email || "No email" }}
                  </span>
                </div>
              </div>
            </section>

            <section class="detail-section">
              <h4>Services</h4>

              <div class="services-list">
                @for (item of selectedAppointment.items; track item.id) {
                  <div class="service-item">
                    <div>
                      <strong>
                        {{ getServiceDetails(item).name }}
                      </strong>

                      <span>
                        {{ getServiceDetails(item).duration_minutes }}
                      </span>
                    </div>

                    <strong>
                      {{ getServiceDetails(item).price }}
                    </strong>
                  </div>
                }
              </div>
            </section>

            @if (selectedAppointment.notes) {
              <section class="detail-section">
                <h4>Notes</h4>

                <p class="appointment-notes">
                  {{ selectedAppointment.notes }}
                </p>
              </section>
            }
          </div>

          <div class="modal-footer">
            @if (canUpdateStatus(selectedAppointment.status)) {
              <div class="status-actions">
                <p>Update appointment status</p>

                <div class="action-buttons">
                  @for (
                    action of getAvailableActions(selectedAppointment.status);
                    track action
                  ) {
                    <button
                      type="button"
                      class="status-action-button"
                      [ngClass]="getActionButtonClass(action)"
                      [disabled]="isUpdatingStatus"
                      (click)="updateAppointmentStatus(action)"
                    >
                      {{ formatActionLabel(action) }}
                    </button>
                  }
                </div>
              </div>
            } @else {
              <p class="no-actions">No further status updates are available.</p>
            }
          </div>
        }
      </div>
    </div>
  }
</div>
